import { useMemo } from 'react';
import { useResponsive, useCookieConsent } from '@emfood/shared';

import Box, { BoxProps } from '@mui/material/Box';

import { BANNER_SYSTEM_MESSAGE } from 'src/config-global';

import { useSettingsContext } from 'src/components/settings';

import { NAV, HEADER, BANNER_HEIGHT, COOKIE_BANNER } from '../config-layout';

// ----------------------------------------------------------------------

const SPACING = 8;

export default function Main({ children, sx, ...other }: BoxProps) {
  const settings = useSettingsContext();

  const lgUp = useResponsive('up', 'lg');

  const isNavHorizontal = settings.themeLayout === 'horizontal';

  const isNavMini = settings.themeLayout === 'mini';

  const systemMassage = useMemo(() => BANNER_SYSTEM_MESSAGE, []);

  // The cookie banner is in normal flow above the layout (CookieOffsetBox pushes everything down
  // by its height), so a full-viewport <main> must give that height back or the page overflows.
  const { shouldDisplayCookieBanner } = useCookieConsent();
  const smDown = useResponsive('down', 'sm');
  const cookieBannerOffset = shouldDisplayCookieBanner
    ? smDown
      ? COOKIE_BANNER.MOBILE
      : COOKIE_BANNER.DESKTOP
    : '0px';
  const minHeight = `calc(100dvh - ${cookieBannerOffset})`;

  if (isNavHorizontal) {
    return (
      <Box
        component="main"
        id="main-content"
        role="main"
        aria-label="Main content"
        sx={{
          minHeight,
          display: 'flex',
          flexDirection: 'column',
          pt: `${HEADER.H_MOBILE + 24 + (systemMassage ? BANNER_HEIGHT.H_MOBILE : 0)}px`,
          pb: 10,
          ...(lgUp && {
            pt: `${HEADER.H_DESKTOP * 2 + 40 + (systemMassage ? BANNER_HEIGHT.H_DESKTOP : 0)}px`,
            pb: 15,
          }),
        }}
      >
        {children}
      </Box>
    );
  }

  return (
    <Box
      component="main"
      id="main-content"
      role="main"
      aria-label="Main content"
      sx={{
        flexGrow: 1,
        minHeight,
        display: 'flex',
        flexDirection: 'column',
        py: `${HEADER.H_MOBILE + SPACING + (systemMassage ? BANNER_HEIGHT.H_MOBILE : 0)}px`,
        ...(lgUp && {
          px: 2,
          py: `${HEADER.H_DESKTOP + SPACING + (systemMassage ? BANNER_HEIGHT.H_DESKTOP : 0)}px`,
          width: `calc(100% - ${NAV.W_VERTICAL}px)`,
          ...(isNavMini && {
            width: `calc(100% - ${NAV.W_MINI}px)`,
          }),
        }),
        ...sx,
      }}
      {...other}
    >
      {children}
    </Box>
  );
}
